import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react-refresh/only-export-components -- библиотечный модуль экспортирует render-функцию */
import { TEXT_SAFE_MARGIN_MM, pageSizeFor, resolveBandImageRatio, resolveLayout, resolveTextSide, spreadSizeFor, textSlotFor, } from '@kitup/book-schema';
import React, {} from 'react';
import { Illustration } from './media.js';
import { SpreadBookPage } from './page.js';
import { prepareSpreadText } from './text.js';
import { LineHighlightText } from './line-highlight-text.js';
function TextSlot({ block, attributes, fontSizePt, slotWidthMm, slotHeightMm, align, style, overflow, }) {
    return (_jsx("div", { "data-text-slot": "", "data-text-position": block.position, "data-text-slot-width-mm": slotWidthMm, "data-text-slot-height-mm": slotHeightMm, "data-text-overflow": overflow ? 'true' : 'false', style: {
            position: 'absolute',
            zIndex: 10,
            width: `${slotWidthMm}mm`,
            height: `${slotHeightMm}mm`,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'none',
            ...style,
        }, children: _jsx(LineHighlightText, { text: block.text, align: align, fontFamily: attributes.typography.fontFamily, fontSizePt: fontSizePt, textColor: block.textColor ?? attributes.typography.defaultTextColor, highlightColor: block.highlightColor ?? attributes.typography.highlight }) }));
}
/** Угловая плашка full-bleed с геометрией ровно из `textSlotFor`. */
export function SpreadHalfOverlay({ side, block, attributes, fontSizePt, overflow = false, }) {
    const slot = textSlotFor('full-bleed', attributes);
    const position = block.position;
    const align = position.endsWith('right') ? 'right' : 'left';
    return (_jsx(TextSlot, { block: block, attributes: attributes, fontSizePt: fontSizePt, slotWidthMm: slot.widthMm, slotHeightMm: slot.heightMm, align: align, overflow: overflow, style: {
            [side === 'left' ? 'left' : 'right']: `${TEXT_SAFE_MARGIN_MM}mm`,
            [position.startsWith('top') ? 'top' : 'bottom']: `${TEXT_SAFE_MARGIN_MM}mm`,
            justifyContent: position.startsWith('top') ? 'flex-start' : 'flex-end',
        } }));
}
/** Full-bleed: иллюстрация на весь разворот, текст — поверх в углах. */
export function SpreadPageTemplate({ scene, attributes, prepared = prepareSpreadText(scene, attributes), showFoldLine = attributes.reading.showFoldLine, }) {
    const spread = spreadSizeFor(attributes);
    return (_jsxs(SpreadBookPage, { attributes: attributes, layoutId: "full-bleed", showFoldLine: showFoldLine, children: [_jsx(Illustration, { src: scene.imageUrl, alt: scene.imageAlt, videoSrc: scene.videoUrl, autoPlayVideo: attributes.reading.autoplayVideo, style: {
                    position: 'absolute',
                    inset: 0,
                    width: `${spread.widthMm}mm`,
                    height: `${spread.heightMm}mm`,
                } }), prepared.blocks.map((block) => {
                const side = block.position.endsWith('right') ? 'right' : 'left';
                return (_jsx(SpreadHalfOverlay, { side: side, block: block, attributes: attributes, fontSizePt: prepared.fontSizePt, overflow: !prepared.fits }, block.slotId));
            })] }));
}
/** Явное имя раскладки для реестра/потребителей. */
export const FullBleedSpreadTemplate = SpreadPageTemplate;
/** Split: одна страница под текст, вторая целиком под портретную картинку. */
export function SplitSpreadTemplate({ scene, attributes, prepared = prepareSpreadText(scene, attributes), showFoldLine = attributes.reading.showFoldLine, }) {
    const page = pageSizeFor(attributes);
    const spread = spreadSizeFor(attributes);
    const block = prepared.blocks[0];
    const textSide = resolveTextSide({ index: scene.index, textBlocks: prepared.blocks }, attributes);
    const imageSide = textSide === 'left' ? 'right' : 'left';
    const slot = textSlotFor('split', attributes, scene);
    return (_jsxs(SpreadBookPage, { attributes: attributes, layoutId: "split", showFoldLine: showFoldLine, children: [_jsx(Illustration, { src: scene.imageUrl, alt: scene.imageAlt, videoSrc: scene.videoUrl, autoPlayVideo: attributes.reading.autoplayVideo, style: {
                    position: 'absolute',
                    top: 0,
                    left: imageSide === 'left' ? 0 : `${spread.widthMm - page.widthMm}mm`,
                    width: `${page.widthMm}mm`,
                    height: `${page.heightMm}mm`,
                } }), block ? (_jsx(TextSlot, { block: block, attributes: attributes, fontSizePt: prepared.fontSizePt, slotWidthMm: slot.widthMm, slotHeightMm: slot.heightMm, align: "center", overflow: !prepared.fits, style: {
                    top: `${TEXT_SAFE_MARGIN_MM}mm`,
                    left: textSide === 'left'
                        ? `${TEXT_SAFE_MARGIN_MM}mm`
                        : `${spread.widthMm - page.widthMm + TEXT_SAFE_MARGIN_MM}mm`,
                    justifyContent: 'center',
                } })) : null] }));
}
/** Band: панорама и отдельная текстовая полоса сверху либо снизу. */
export function BandSpreadTemplate({ scene, attributes, prepared = prepareSpreadText(scene, attributes), showFoldLine = attributes.reading.showFoldLine, }) {
    const spread = spreadSizeFor(attributes);
    const ratio = resolveBandImageRatio(attributes, scene);
    const imageHeightMm = spread.heightMm * ratio;
    const block = prepared.blocks[0];
    const textPosition = block?.position === 'top' ? 'top' : 'bottom';
    const slot = textSlotFor('band', attributes, scene);
    return (_jsxs(SpreadBookPage, { attributes: attributes, layoutId: "band", showFoldLine: showFoldLine, children: [_jsx(Illustration, { src: scene.imageUrl, alt: scene.imageAlt, videoSrc: scene.videoUrl, autoPlayVideo: attributes.reading.autoplayVideo, style: {
                    position: 'absolute',
                    left: 0,
                    top: textPosition === 'top' ? `${spread.heightMm - imageHeightMm}mm` : 0,
                    width: `${spread.widthMm}mm`,
                    height: `${imageHeightMm}mm`,
                } }), block ? (_jsx(TextSlot, { block: block, attributes: attributes, fontSizePt: prepared.fontSizePt, slotWidthMm: slot.widthMm, slotHeightMm: slot.heightMm, align: "center", overflow: !prepared.fits, style: {
                    left: `${TEXT_SAFE_MARGIN_MM}mm`,
                    top: textPosition === 'top'
                        ? `${TEXT_SAFE_MARGIN_MM}mm`
                        : `${imageHeightMm + TEXT_SAFE_MARGIN_MM}mm`,
                    justifyContent: 'center',
                } })) : null] }));
}
const SPREAD_COMPONENTS = {
    split: SplitSpreadTemplate,
    'full-bleed': SpreadPageTemplate,
    band: BandSpreadTemplate,
};
/** Единая чистая точка входа: выбор компонента только через `resolveLayout`. */
export function renderSpread(scene, attributes) {
    const layoutId = resolveLayout(scene, attributes);
    const Component = SPREAD_COMPONENTS[layoutId];
    return _jsx(Component, { scene: scene, attributes: attributes });
}
//# sourceMappingURL=spread-templates.js.map